import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { isValidIsoDate, isValidMonth, todayIn, monthOf } from '@tt/shared';
import { AdminActivitiesPage } from '@/features/admin/admin-activities-page';
import { AdminAuditLogPage } from '@/features/admin/admin-audit-log-page';
import { AdminGroupDetailPage, AdminGroupsPage } from '@/features/admin/admin-groups-page';
import { AdminMaintenancePage } from '@/features/admin/admin-maintenance-page';
import { AdminOrgDashboardPage } from '@/features/admin/admin-org-dashboard-page';
import { AdminProjectsPage } from '@/features/admin/admin-projects-page';
import { AdminReportsPage } from '@/features/admin/admin-reports-page';
import { AdminSettingsPage } from '@/features/admin/admin-settings-page';
import { AdminUsersPage } from '@/features/admin/admin-users-page';
import { AdminTimesheetPage } from '@/features/admin/admin-timesheet-page';
import { AppShell } from '@/components/app-shell';
import { AcceptInvitePage } from '@/features/auth/accept-invite-page';
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page';
import { LoginPage } from '@/features/auth/login-page';
import { ResetPasswordPage } from '@/features/auth/reset-password-page';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { ProfilePage } from '@/features/settings/profile-page';
import { SecurityPage } from '@/features/settings/security-page';
import { DayView } from '@/features/time/day-view';
import { MonthView } from '@/features/time/month-view';
import { WeekView } from '@/features/time/week-view';
import { meQuery } from '@/lib/queries';
import { NotFoundPage, RootErrorPage } from '@/features/misc-pages';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
  errorComponent: RootErrorPage,
});

// ---------------------------------------------------------------- public

const redirectSearch = z.object({ redirect: z.string().optional() });
const tokenSearch = z.object({ token: z.string().optional() });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: redirectSearch,
  component: LoginPage,
});
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: tokenSearch,
  component: ResetPasswordPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: tokenSearch,
  component: AcceptInvitePage,
});

// ---------------------------------------------------------------- authenticated

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      return { me };
    } catch {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ to: '/time/week/$date', params: { date: todayIn(context.me.timeZone) } });
  },
});

const timeRoute = createRoute({ getParentRoute: () => appRoute, path: 'time' });
const timeIndexRoute = createRoute({
  getParentRoute: () => timeRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ to: '/time/week/$date', params: { date: todayIn(context.me.timeZone) } });
  },
});
const dayRoute = createRoute({
  getParentRoute: () => timeRoute,
  path: 'day/$date',
  beforeLoad: ({ params, context }) => {
    if (!isValidIsoDate(params.date))
      throw redirect({ to: '/time/day/$date', params: { date: todayIn(context.me.timeZone) } });
  },
  component: DayView,
});
const weekRoute = createRoute({
  getParentRoute: () => timeRoute,
  path: 'week/$date',
  beforeLoad: ({ params, context }) => {
    if (!isValidIsoDate(params.date))
      throw redirect({ to: '/time/week/$date', params: { date: todayIn(context.me.timeZone) } });
  },
  component: WeekView,
});
const monthRoute = createRoute({
  getParentRoute: () => timeRoute,
  path: 'month/$month',
  beforeLoad: ({ params, context }) => {
    if (!isValidMonth(params.month))
      throw redirect({
        to: '/time/month/$month',
        params: { month: monthOf(todayIn(context.me.timeZone)) },
      });
  },
  component: MonthView,
});

export const dashboardSearch = z.object({
  period: z.enum(['day', 'week', 'month']).default('week').catch('week'),
  date: z.string().optional().catch(undefined),
});
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'dashboard',
  validateSearch: dashboardSearch,
  component: DashboardPage,
});

// Admin-only section. The server authorizes every request as well; this only hides the UI.
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin',
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'ADMIN') throw redirect({ to: '/' });
  },
});
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/dashboard', search: {} });
  },
});

/** Every filter lives in the URL so an admin can share exactly what they are looking at. */
export const adminOrgDashboardSearch = z.object({
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  userIds: z.string().optional().catch(undefined),
  groupIds: z.string().optional().catch(undefined),
  projectIds: z.string().optional().catch(undefined),
  activityIds: z.string().optional().catch(undefined),
  tab: z.enum(['overview', 'users', 'missing']).optional().catch(undefined),
});
const adminOrgDashboardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'dashboard',
  validateSearch: adminOrgDashboardSearch,
  component: AdminOrgDashboardPage,
});

export const adminUsersSearch = z.object({
  q: z.string().optional().catch(undefined),
  role: z.enum(['USER', 'ADMIN']).optional().catch(undefined),
  status: z.enum(['ACTIVE', 'INVITED', 'INACTIVE']).optional().catch(undefined),
  groupId: z.string().uuid().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'users',
  validateSearch: adminUsersSearch,
  component: AdminUsersPage,
});

export const adminGroupsSearch = z.object({
  q: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminGroupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'groups',
  validateSearch: adminGroupsSearch,
  component: AdminGroupsPage,
});
const adminGroupDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'groups/$groupId',
  component: AdminGroupDetailPage,
});

export const adminActivitiesSearch = z.object({
  q: z.string().optional().catch(undefined),
  type: z.enum(['SHARED', 'PERSONAL']).optional().catch(undefined),
  status: z.enum(['ALL', 'ACTIVE', 'ARCHIVED']).optional().catch(undefined),
  /** One project's activities, or `unassigned` for the ones belonging to no project at all. */
  project: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminActivitiesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'activities',
  validateSearch: adminActivitiesSearch,
  component: AdminActivitiesPage,
});

export const adminAuditSearch = z.object({
  action: z.string().optional().catch(undefined),
  entityType: z.string().optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'audit-log',
  validateSearch: adminAuditSearch,
  component: AdminAuditLogPage,
});

export const adminReportsSearch = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminReportsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reports',
  validateSearch: adminReportsSearch,
  component: AdminReportsPage,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'settings',
  component: AdminSettingsPage,
});

const adminMaintenanceRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'maintenance',
  component: AdminMaintenancePage,
});
export const adminTimesheetSearch = z.object({
  userId: z.string().uuid().optional().catch(undefined),
  date: z.string().optional().catch(undefined),
});
const adminTimesheetRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'timesheets',
  validateSearch: adminTimesheetSearch,
  component: AdminTimesheetPage,
});

export const adminProjectsSearch = z.object({
  q: z.string().optional().catch(undefined),
  status: z.enum(['ALL', 'ACTIVE', 'ARCHIVED']).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
const adminProjectsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'projects',
  validateSearch: adminProjectsSearch,
  component: AdminProjectsPage,
});

const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings' });
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/profile' });
  },
});
const profileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'profile',
  component: ProfilePage,
});
const securityRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'security',
  component: SecurityPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotRoute,
  resetRoute,
  inviteRoute,
  appRoute.addChildren([
    indexRoute,
    timeRoute.addChildren([timeIndexRoute, dayRoute, weekRoute, monthRoute]),
    dashboardRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminOrgDashboardRoute,
      adminMaintenanceRoute,
      adminUsersRoute,
      adminGroupsRoute,
      adminGroupDetailRoute,
      adminProjectsRoute,
      adminActivitiesRoute,
      adminTimesheetRoute,
      adminReportsRoute,
      adminAuditRoute,
      adminSettingsRoute,
    ]),
    settingsRoute.addChildren([settingsIndexRoute, profileRoute, securityRoute]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
